import Loader from "@components/Loader/Loader";
import EmptyState from "@components/invoiceForm";
import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactPaginate from "react-paginate";
import { useSelector } from "react-redux";
import { useGetClientsQuery } from "store/client/clientApi";
import { useGetQuotesQuery } from "store/quote/quoteApi";
import QuoteList from "./QuoteList";
import "./index.scss";
import { StatusResponse } from "./Quotes.contants";

const QuoteListPage = () => {
  const { t } = useTranslation();
  const currentUser = useSelector((state) => state.auth.user);
  const companyId = currentUser?.companyId?._id;

  const [selectedStatus, setSelectedStatus] = useState(StatusResponse.ALL);
  const [clientNameFilter, setClientNameFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const itemsPerPage = 10;

  const { data: allQuotesData, isLoading } = useGetQuotesQuery({
    companyId,
    page: 1,
    limit: 1000,
  });

  const { data: clientsData, isLoading: isClientsLoading } =
    useGetClientsQuery(companyId);

  useEffect(() => {
    setCurrentPage(0);
  }, [selectedStatus, clientNameFilter, startDate]);

  // 🔍 Filtrage local côté front
  const filteredQuotes = useMemo(() => {
    if (!allQuotesData?.data) return [];

    return allQuotesData.data.filter((quote) => {
      const matchClient =
        !clientNameFilter || quote.idClient?._id === clientNameFilter;

      const matchStatus =
        selectedStatus === StatusResponse.ALL ||
        quote.status === selectedStatus;

      const matchStartDate =
        !startDate ||
        (quote.startDate && quote.startDate.slice(0, 10) === startDate); // Compare YYYY-MM-DD

      return matchClient && matchStatus && matchStartDate;
    });
  }, [allQuotesData, selectedStatus, clientNameFilter, startDate]);

  const pageCount = Math.ceil(filteredQuotes.length / itemsPerPage);
  const paginatedQuotes = useMemo(() => {
    const start = currentPage * itemsPerPage;
    return filteredQuotes.slice(start, start + itemsPerPage);
  }, [filteredQuotes, currentPage]);

  const handlePageClick = (event) => {
    setCurrentPage(event.selected);
  };

  return (
    <div className="quote-layout d-flex">
      <div className="quote-page p-3 flex-grow-1">
        <header className="quote-header d-flex justify-between align-items-center">
          <h1 className="p-3">{t("quote.title")}</h1>
        </header>

        {/* Filtres */}
        <div className="quote-date-filter p-3 d-flex gap-3 align-items-center">
          <label>
            {t("quote.start_date")}
            <input
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              className="form-control"
            />
          </label>

          <label>
            {t("quote.client_name") || "Client"}
            <select
              value={clientNameFilter}
              onChange={(e) => setClientNameFilter(e.target.value)}
              disabled={isClientsLoading}
              className="form-select">
              <option value="">Tous les clients</option>
              {clientsData?.data?.map((client) => (
                <option key={client._id} value={client._id}>
                  {client.name}
                </option>
              ))}
            </select>
          </label>
        </div>

        {/* Tabs statut */}
        <nav className="quote-tabs p-3">
          <ul className="d-flex gap-3">
            {Object.values(StatusResponse).map((status) => (
              <li
                key={status}
                className={selectedStatus === status ? "active" : ""}
                onClick={() => setSelectedStatus(status)}
                style={{ cursor: "pointer" }}>
                {t(`quote.status.${status}`) || status}
              </li>
            ))}
          </ul>
        </nav>

        {/* Liste ou vide */}
        {isLoading ? (
          <Loader />
        ) : paginatedQuotes.length > 0 ? (
          <>
            <QuoteList quotes={paginatedQuotes} />
            <div className="d-flex justify-content-center mt-4">
              <ReactPaginate
                previousLabel={"←"}
                nextLabel={"→"}
                breakLabel={"..."}
                pageCount={pageCount}
                onPageChange={handlePageClick}
                containerClassName={"pagination"}
                activeClassName={"active"}
                forcePage={currentPage}
              />
            </div>
          </>
        ) : (
          <EmptyState type="quote" />
        )}
      </div>
    </div>
  );
};

export default QuoteListPage;
